import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { CargoRequestService, CargoRequest, SearchCargoRequestsRequest } from '../../../services/api/cargo-request';
import { DealService, CargoDeal } from '../../../services/api/deal';
import { CustomsCalculationsService, CustomsCalculation, SearchCustomsCalculationsRequest } from '../../../services/api/customs-calculations';
import { DictionaryService, CountryDto } from '../../../services/api/dictionary';
import { Subscription } from 'rxjs';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { PageResponse } from '../../../services/api/page-response';
import { CalculationsListComponent } from '../../shared/customs-calculations/calculations-list/calculations-list';
import { SuppliesListComponent } from '../../shared/customs-calculations/supplies-list/supplies-list';
import { DocumentTypesComponent } from '../document-types/document-types';
import { DocNumberPipe } from '../../../services/request-number';

@Component({
  selector: 'app-customs-broker-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, PaginationComponent, DatePipe, CalculationsListComponent, SuppliesListComponent, DocumentTypesComponent, DocNumberPipe],
  templateUrl: './customs-broker-dashboard.html',
  styleUrl: './customs-broker-dashboard.scss'
})
export class CustomsBrokerDashboardComponent implements OnInit, OnDestroy {
  activeTab: 'requests' | 'deals' | 'calculations' | 'supplies' | 'settings' = 'supplies';
  /** Подраздел настроек: null — меню, иначе ключ раздела (напр. document-types). */
  settingsSection: string | null = null;

  titleText = 'Личный кабинет брокера';
  loadingText = 'Загрузка...';

  // Requests
  requests: CargoRequest[] = [];
  loadingRequests = false;
  errorRequests: string | null = null;
  requestsPage: PageResponse<CargoRequest> | null = null;
  currentRequestsPage = 0;
  pageSize = 20;

  // Deals
  deals: CargoDeal[] = [];
  loadingDeals = false;
  errorDeals: string | null = null;
  dealsPage: PageResponse<CargoDeal> | null = null;
  currentDealsPage = 0;

  // Calculations (all users for broker)
  calculations: CustomsCalculation[] = [];
  loadingCalculations = false;
  errorCalculations: string | null = null;
  calculationsPage: PageResponse<CustomsCalculation> | null = null;
  currentCalculationsPage = 0;
  currentCalculationsFilters: SearchCustomsCalculationsRequest = {};

  // Supplies (calculations in status IN_PROCESSING, for all users)
  supplies: CustomsCalculation[] = [];
  loadingSupplies = false;
  errorSupplies: string | null = null;
  suppliesPage: PageResponse<CustomsCalculation> | null = null;
  currentSuppliesPage = 0;
  currentSuppliesFilters: SearchCustomsCalculationsRequest = {};

  countries: CountryDto[] = [];

  requestsTabText = 'Запросы';
  dealsTabText = 'Сделки';
  requestNumberText = 'Номер запроса';
  companyNameText = 'Компания';
  routeText = 'Маршрут';
  totalWeightText = 'Общий вес';
  totalVolumeText = 'Общий объем';
  statusText = 'Статус';
  createdAtText = 'Дата создания';
  noRequestsText = 'Нет запросов';
  noDealsText = 'Нет сделок';
  viewText = 'Просмотр';
  calculationStatusText = 'Статус расчета';
  dealIdText = '№ сделки';
  cargoOwnerText = 'Грузовладелец';
  forwarderText = 'Экспедитор';
  calculationsTabText = 'Расчеты';
  suppliesTabText = 'Поставки';
  suppliesTitleText = 'Поставки';
  suppliesSubtitleText = 'Расчёты в статусе «В обработке», переданные на сопровождение';
  suppliesEmptyText = 'Поставок в обработке пока нет';
  settingsTabText = 'Настройки';
  settingsTitleText = 'Настройки';
  settingsDocumentTypesText = 'Типы разрешительных документов';
  settingsDocumentTypesDescText = 'Список сертификатов и документов для проверки AI';
  settingsBackText = 'Назад к настройкам';

  private subscriptions = new Subscription();

  constructor(
    private languageService: LanguageService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private cargoRequestService: CargoRequestService,
    private dealService: DealService,
    private customsCalculationsService: CustomsCalculationsService,
    private dictionaryService: DictionaryService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        this.router.navigate(['/login']);
        return;
      }

      const user = this.authService.getUser();
      if (user?.role?.code !== 'ROLE_CUSTOMS_BROKER') {
        this.router.navigate(['/']);
        return;
      }

      this.subscriptions.add(
        this.route.paramMap.subscribe(params => {
          const url = this.router.url;
          if (url.includes('/dashboard/settings')) {
            this.activeTab = 'settings';
            this.settingsSection = params.get('section');
          } else {
            const tab = params.get('tab') as 'requests' | 'deals' | 'calculations' | 'supplies' | 'settings';
            if (tab && ['requests', 'deals', 'calculations', 'supplies', 'settings'].includes(tab)) {
              this.activeTab = tab;
              this.settingsSection = null;
              this.loadTabData(tab);
            }
          }
          this.cdr.markForCheck();
        })
      );

      this.loadCountries();
      this.updateTranslations();

      this.subscriptions.add(
        this.languageService.getTranslations().subscribe(() => {
          this.updateTranslations();
          this.cdr.markForCheck();
        })
      );
    }, 0);
  }

  private loadTabData(tab: 'requests' | 'deals' | 'calculations' | 'supplies' | 'settings'): void {
    if (tab === 'requests' && this.requests.length === 0 && !this.loadingRequests) {
      this.loadRequests();
    } else if (tab === 'deals' && this.deals.length === 0 && !this.loadingDeals) {
      this.loadDeals();
    } else if (tab === 'calculations' && this.calculations.length === 0 && !this.loadingCalculations) {
      this.loadCalculations();
    } else if (tab === 'supplies' && this.supplies.length === 0 && !this.loadingSupplies) {
      this.loadSupplies();
    }
    // settings — данные грузит вложенный DocumentTypesComponent
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  switchTab(tab: 'requests' | 'deals' | 'calculations' | 'supplies' | 'settings'): void {
    if (tab === 'settings') {
      this.router.navigate(['/customs-broker/dashboard/settings']);
      return;
    }
    this.router.navigate(['/customs-broker/dashboard', tab]);
  }

  openSettingsSection(section: string): void {
    this.router.navigate(['/customs-broker/dashboard/settings', section]);
  }

  backToSettingsMenu(): void {
    this.router.navigate(['/customs-broker/dashboard/settings']);
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private loadCountries(): void {
    this.subscriptions.add(
      this.dictionaryService.getAllCountries().subscribe({
        next: (countries: CountryDto[]) => {
          this.countries = countries;
          this.cdr.markForCheck();
        },
        error: () => {}
      })
    );
  }

  loadRequests(): void {
    this.loadRequestsPage(this.currentRequestsPage);
  }

  loadRequestsPage(page: number): void {
    this.loadingRequests = true;
    this.errorRequests = null;
    this.currentRequestsPage = page;

    // Все запросы всех грузовладельцев (без фильтра по таможне)
    const filter: SearchCargoRequestsRequest = {};

    this.subscriptions.add(
      this.cargoRequestService.searchCargoRequests(filter, page, this.pageSize).subscribe({
        next: (pageResponse) => {
          this.requestsPage = pageResponse;
          this.requests = pageResponse.content;
          this.loadingRequests = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          if (err.status === 401) {
            this.errorRequests = 'Сессия истекла. Пожалуйста, войдите снова.';
          } else if (err.status === 403) {
            this.errorRequests = 'Ошибка доступа (403).';
          } else {
            this.errorRequests = 'Ошибка при загрузке запросов';
          }
          this.loadingRequests = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  onRequestsPageChange(page: number): void {
    this.loadRequestsPage(page);
  }

  viewRequest(requestId: number): void {
    this.router.navigate(['/customs-broker/request', requestId]);
  }

  loadDeals(): void {
    this.loadDealsPage(this.currentDealsPage);
  }

  loadDealsPage(page: number): void {
    this.loadingDeals = true;
    this.errorDeals = null;
    this.currentDealsPage = page;

    this.subscriptions.add(
      this.dealService.getBrokerDealsPaginated(page, this.pageSize).subscribe({
        next: (pageResponse) => {
          this.dealsPage = pageResponse;
          this.deals = pageResponse.content;
          this.loadingDeals = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          if (err.status === 401) {
            this.errorDeals = 'Сессия истекла. Пожалуйста, войдите снова.';
          } else if (err.status === 403) {
            this.errorDeals = 'Ошибка доступа (403).';
          } else {
            this.errorDeals = 'Ошибка при загрузке сделок';
          }
          this.loadingDeals = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  onDealsPageChange(page: number): void {
    this.loadDealsPage(page);
  }

  viewDeal(dealId: number): void {
    this.router.navigate(['/customs-broker/deal', dealId]);
  }

  loadCalculations(): void {
    this.loadCalculationsPage(this.currentCalculationsPage, this.currentCalculationsFilters);
  }

  loadCalculationsPage(page: number, filters: SearchCustomsCalculationsRequest): void {
    this.loadingCalculations = true;
    this.errorCalculations = null;
    this.currentCalculationsPage = page;
    this.currentCalculationsFilters = filters;

    this.subscriptions.add(
      this.customsCalculationsService.searchCalculationsForBroker(filters, page, this.pageSize).subscribe({
        next: (pageResponse) => {
          this.calculationsPage = pageResponse;
          this.calculations = pageResponse.content;
          this.loadingCalculations = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading calculations:', err);
          this.errorCalculations = 'Ошибка при загрузке расчетов';
          this.loadingCalculations = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  onCalculationsPageChange(page: number): void {
    this.loadCalculationsPage(page, this.currentCalculationsFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onCalculationsFilterChange(filters: SearchCustomsCalculationsRequest): void {
    this.currentCalculationsPage = 0;
    this.loadCalculationsPage(0, filters);
  }

  onViewCalculation(id: number): void {
    this.router.navigate(['/calculations', id]);
  }

  loadSupplies(): void {
    this.loadSuppliesPage(this.currentSuppliesPage, this.currentSuppliesFilters);
  }

  loadSuppliesPage(page: number, filters: SearchCustomsCalculationsRequest): void {
    this.loadingSupplies = true;
    this.errorSupplies = null;
    this.currentSuppliesPage = page;
    this.currentSuppliesFilters = filters;

    this.subscriptions.add(
      this.customsCalculationsService
        .searchCalculationsForBroker({ ...filters, status: 'IN_PROCESSING' }, page, this.pageSize)
        .subscribe({
          next: (pageResponse) => {
            this.suppliesPage = pageResponse;
            this.supplies = pageResponse.content;
            this.loadingSupplies = false;
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error loading supplies:', err);
            this.errorSupplies = 'Ошибка при загрузке поставок';
            this.loadingSupplies = false;
            this.cdr.markForCheck();
          }
        })
    );
  }

  onSuppliesPageChange(page: number): void {
    this.loadSuppliesPage(page, this.currentSuppliesFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onSuppliesFilterChange(filters: SearchCustomsCalculationsRequest): void {
    this.currentSuppliesPage = 0;
    this.loadSuppliesPage(0, filters);
  }

  onOpenSupply(id: number): void {
    this.router.navigate(['/calculations', id, 'process']);
  }

  getRoute(request: CargoRequest): string {
    const fromCountry = this.countries.find(c => c.id === request.fromCountryId);
    const toCountry = this.countries.find(c => c.id === request.toCountryId);
    const from = fromCountry ? fromCountry.nameRu : request.fromCity || '-';
    const to = toCountry ? toCountry.nameRu : request.toCity || '-';
    return `${from} → ${to}`;
  }

  getTotalWeight(request: CargoRequest): number {
    if (!request.products || request.products.length === 0) return 0;
    return request.products.reduce((total, product) => total + (product.totalWeightKg || 0), 0);
  }

  getTotalVolume(request: CargoRequest): number {
    if (!request.products || request.products.length === 0) return 0;
    return request.products.reduce((total, product) => {
      if (product.volumeM3) {
        return total + product.volumeM3;
      }
      if (product.lengthMm && product.widthMm && product.heightMm) {
        return total + (product.lengthMm * product.widthMm * product.heightMm) / 1000000000 * (product.quantity || 1);
      }
      return total;
    }, 0);
  }

  formatWeight(kg: number): string {
    if (kg >= 1000) {
      return `${(kg / 1000).toFixed(2)} т`;
    }
    return `${kg.toFixed(2)} кг`;
  }

  formatVolume(m3: number): string {
    return `${m3.toFixed(3)} м³`;
  }

  getCalculationStatus(request: CargoRequest): string {
    if (request.customsCalculationCompleted) {
      return 'Завершен';
    }
    if (request.customsCalculationStarted) {
      return 'В работе';
    }
    return 'Ожидает';
  }

  getDealStatusText(status: string): string {
    const map: Record<string, string> = {
      CREATED: 'Создана',
      IN_PROGRESS: 'В процессе',
      COMPLETED: 'Завершена'
    };
    return map[status] || status;
  }

  private updateTranslations(): void {
    this.titleText = this.t('customsBroker.dashboard.title', 'Личный кабинет брокера');
    this.loadingText = this.t('customsBroker.dashboard.loading', 'Загрузка...');
    this.requestNumberText = this.t('cargoOwner.dashboard.requestNumber', 'Номер запроса');
    this.companyNameText = this.t('customsBroker.dashboard.companyName', 'Компания');
    this.routeText = this.t('cargoOwner.dashboard.route', 'Маршрут');
    this.totalWeightText = this.t('cargoOwner.dashboard.totalWeight', 'Общий вес');
    this.totalVolumeText = this.t('cargoOwner.dashboard.totalVolume', 'Общий объем');
    this.statusText = this.t('cargoOwner.dashboard.status', 'Статус');
    this.createdAtText = this.t('cargoOwner.dashboard.createdAt', 'Дата создания');
    this.noRequestsText = this.t('cargoOwner.dashboard.noRequests', 'Нет запросов');
    this.viewText = this.t('customsBroker.dashboard.view', this.t('cargoOwner.dashboard.view', 'Просмотр'));
    this.calculationStatusText = this.t('customsBroker.dashboard.calculationStatus', 'Статус расчета');
    this.requestsTabText = this.t('customsBroker.dashboard.requestsTab', 'Запросы');
    this.dealsTabText = this.t('customsBroker.dashboard.dealsTab', 'Сделки');
    this.dealIdText = this.t('customsBroker.dashboard.dealId', '№ сделки');
    this.cargoOwnerText = this.t('customsBroker.dashboard.cargoOwner', 'Грузовладелец');
    this.forwarderText = this.t('customsBroker.dashboard.forwarder', 'Экспедитор');
    this.noDealsText = this.t('customsBroker.dashboard.noDeals', 'Нет сделок');
    this.calculationsTabText = this.t(
      'customsBroker.dashboard.calculationsTab',
      this.t('cargoOwner.dashboard.calculationsTab', 'Расчеты')
    );
    this.suppliesTabText = this.t(
      'customsBroker.dashboard.suppliesTab',
      this.t('cargoOwner.dashboard.suppliesTab', 'Поставки')
    );
    this.suppliesTitleText = this.t('customsBroker.dashboard.suppliesTitle', this.suppliesTitleText);
    this.suppliesSubtitleText = this.t('customsBroker.dashboard.suppliesSubtitle', this.suppliesSubtitleText);
    this.suppliesEmptyText = this.t('customsBroker.dashboard.suppliesEmpty', this.suppliesEmptyText);
    this.settingsTabText = this.t('customsBroker.dashboard.settingsTab', 'Настройки');
    this.settingsTitleText = this.t('customsBroker.dashboard.settingsTitle', 'Настройки');
    this.settingsDocumentTypesText = this.t(
      'customsBroker.dashboard.settingsDocumentTypes',
      'Типы разрешительных документов'
    );
    this.settingsDocumentTypesDescText = this.t(
      'customsBroker.dashboard.settingsDocumentTypesDesc',
      'Список сертификатов и документов для проверки AI'
    );
    this.settingsBackText = this.t('customsBroker.dashboard.settingsBack', 'Назад к настройкам');
    this.cdr.markForCheck();
  }
}
